import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, DollarSign, Check, User, Clock, MessageSquare } from 'lucide-react';
import axios from 'axios';

/**
 * 六爻赏金管理页面
 * 用于求助类型的发布，发布者可以选择评论并分配悬赏金额
 */
const LiuyaoReward = () => {
  const { id } = useParams(); // chart_id
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [chartInfo, setChartInfo] = useState(null);
  const [comments, setComments] = useState([]);
  const [rewardAmounts, setRewardAmounts] = useState({}); // { commentId: amount }
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, [id]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.get(`/api/liuyao/${id}/reward-info`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setChartInfo(response.data.chart);
        setComments(response.data.comments || []);
        
        // 初始化已分配的金额
        const amounts = {};
        (response.data.comments || []).forEach(c => {
          if (c.reward_amount > 0) {
            amounts[c.id] = c.reward_amount;
          }
        });
        setRewardAmounts(amounts);
      }
    } catch (error) {
      console.error('获取数据失败:', error);
      alert('获取数据失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  // 计算已分配总额
  const totalAllocated = Object.values(rewardAmounts).reduce((sum, amt) => sum + (parseFloat(amt) || 0), 0);
  const remainingAmount = (chartInfo?.payment_amount || 0) - totalAllocated;

  // 更新某个评论的分配金额
  const handleAmountChange = (commentId, value) => {
    const numValue = parseFloat(value) || 0;
    setRewardAmounts(prev => ({
      ...prev,
      [commentId]: numValue
    }));
  };

  // 确认评论并分配金额
  const handleConfirmReward = async (commentId) => {
    const amount = rewardAmounts[commentId] || 0;
    
    if (amount <= 0) {
      alert('请输入分配金额');
      return;
    }
    
    if (amount > remainingAmount + (rewardAmounts[commentId] || 0)) {
      alert('分配金额超出剩余可分配金额');
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`/api/liuyao/reward/${commentId}`, {
        amount: amount
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        alert('分配成功！');
        fetchData(); // 刷新数据
      } else {
        alert('操作失败: ' + response.data.message);
      }
    } catch (error) {
      alert('操作失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  // 取消确认
  const handleCancelReward = async (commentId) => {
    if (!window.confirm('确定要取消该评论的悬赏分配吗？')) return;

    setSubmitting(true);
    try {
      const token = localStorage.getItem('access_token');
      const response = await axios.post(`/api/liuyao/reward/${commentId}/cancel`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        alert('已取消分配');
        fetchData();
      } else {
        alert('操作失败: ' + response.data.message);
      }
    } catch (error) {
      alert('操作失败: ' + (error.response?.data?.message || error.message));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (!chartInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <p className="text-gray-500 mb-4">未找到相关信息</p>
          <button onClick={() => navigate(-1)} className="text-indigo-600 hover:underline">
            返回
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 px-4">
      <div className="max-w-3xl mx-auto">
        {/* 顶部导航 */}
        <div className="flex items-center mb-6">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
          >
            <ArrowLeft className="w-5 h-5 mr-1" />
            返回
          </button>
        </div>

        {/* 卦例信息卡片 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-6">
          <h1 className="text-xl font-bold text-gray-900 mb-4">积分管理</h1>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500">占事：</span>
              <span className="font-medium text-gray-900">{chartInfo.zhanti}</span>
            </div>
            <div>
              <span className="text-gray-500">类型：</span>
              <span className="font-medium text-gray-900">{chartInfo.zhanti_type}</span>
            </div>
          </div>

          {/* 悬赏金额统计 */}
          <div className="mt-6 p-4 bg-gradient-to-r from-orange-50 to-yellow-50 rounded-lg border border-orange-100">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <p className="text-xs text-gray-500 mb-1">总悬赏积分</p>
                <p className="text-xl font-bold text-orange-600">{chartInfo.payment_amount?.toFixed(2) || '0.00'} 分</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">已分配</p>
                <p className="text-xl font-bold text-green-600">{totalAllocated.toFixed(2)} 分</p>
              </div>
              <div>
                <p className="text-xs text-gray-500 mb-1">剩余可分配</p>
                <p className={`text-xl font-bold ${remainingAmount > 0 ? 'text-indigo-600' : 'text-gray-400'}`}>
                  {remainingAmount.toFixed(2)} 分
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* 评论列表 */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100">
            <h2 className="text-lg font-bold text-gray-900 flex items-center">
              <MessageSquare className="w-5 h-5 text-indigo-500 mr-2" />
              评论列表
              <span className="ml-2 text-sm font-normal text-gray-500">
                共 {comments.length} 条评论
              </span>
            </h2>
          </div>

          {comments.length > 0 ? (
            <div className="divide-y divide-gray-50">
              {comments.map((comment) => (
                <div key={comment.id} className="p-4 sm:p-6">
                  {/* 评论者信息 */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-indigo-600" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900">{comment.user_nickname || '匿名用户'}</span>
                        {comment.is_confirmed && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                            <Check className="w-3 h-3 mr-1" />
                            已确认
                          </span>
                        )}
                      </div>
                      <div className="flex items-center text-xs text-gray-400 mt-1">
                        <Clock className="w-3 h-3 mr-1" />
                        {comment.comment_time ? new Date(comment.comment_time).toLocaleString('zh-CN') : '—'}
                      </div>
                    </div>
                  </div>

                  {/* 评论内容 */}
                  <div className="bg-gray-50 rounded-lg p-3 mb-4 text-sm text-gray-700">
                    {comment.content}
                  </div>

                  {/* 分配金额操作 */}
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2">
                      <DollarSign className="w-4 h-4 text-orange-500" />
                      <span className="text-sm text-gray-500">分配积分：</span>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={rewardAmounts[comment.id] || ''}
                        onChange={(e) => handleAmountChange(comment.id, e.target.value)}
                        disabled={comment.is_confirmed}
                        className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:ring-indigo-500 focus:border-indigo-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
                        placeholder="0.00"
                      />
                      <span className="text-sm text-gray-500">分</span>
                    </div>

                    {comment.is_confirmed ? (
                      <button
                        onClick={() => handleCancelReward(comment.id)}
                        disabled={submitting}
                        className="px-3 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-lg hover:bg-red-100 transition-colors disabled:opacity-50"
                      >
                        取消确认
                      </button>
                    ) : (
                      <button
                        onClick={() => handleConfirmReward(comment.id)}
                        disabled={submitting || !rewardAmounts[comment.id]}
                        className="px-3 py-1.5 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        确认分配
                      </button>
                    )}
                  </div>

                  {/* 已分配金额显示 */}
                  {comment.is_confirmed && comment.reward_amount > 0 && (
                    <div className="mt-3 text-sm text-green-600 flex items-center">
                      <Check className="w-4 h-4 mr-1" />
                      已分配 {parseFloat(comment.reward_amount).toFixed(2)} 分
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="p-6 text-center text-gray-500">
              <MessageSquare className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-sm">暂无评论</p>
              <p className="text-xs text-gray-400 mt-1">等待其他用户回复您的求助</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiuyaoReward;
